import type { ThemeOptions } from '@mui/material/styles';
import { createTheme } from '@mui/material/styles';
import type {} from '@mui/x-data-grid/themeAugmentation';
import { GridBaseAutocomplete } from './gridBaseAutocomplete';

// Warm visual language — "Option 04: Human"
const colors = {
  black: '#2a2018', // ink
  brandPrimary: '#c05a3a', // terracotta
  cornFlowerBlue: '#f0d5c7', // light terracotta
  darkerBlue: '#a04a2e', // dark terracotta
  deepPurple: '#6b5b8a', // dusk
  goldFont: '#D4AF37',
  green: '#3d6149', // dark sage
  grey: '#d8cfc0', // tan
  greyDark: '#8a7d6e', // mid
  greyDarker: '#4a3f33', // brown
  greyLight: '#f2ede4', // sand
  lightBlue: '#b5a898', // light-mid
  red: '#da071c',
  successGreen: '#4e7c5f', // sage
  white: '#ffffff',
  yellow: '#ffc01e',
  // Named warm palette
  cream: '#faf8f4',
  dusk: '#6b5b8a',
  ink: '#2a2018',
  lightMid: '#b5a898',
  mid: '#8a7d6e',
  sage: '#4e7c5f',
  sand: '#f2ede4',
  tan: '#d8cfc0',
  terracotta: '#c05a3a',
  warm: '#ebe4d8',
};

const breakpoints = {
  values: {
    lg: 1024,
    md: 720,
    sm: 576,
    xl: 1344,
    xs: 0,
  },
};

const spacing = 8; // Base spacing unit (8px)

const FONT_DISPLAY = '"Fraunces", serif';
const FONT_BODY = '"Plus Jakarta Sans", "Helvetica", "Arial", sans-serif';
const FONT_MONO = '"Azeret Mono", monospace';

const typography = {
  body1: {
    color: '#4a3f33',
    fontSize: '1rem', // 16px
    lineHeight: 1.5,
  },
  body2: {
    color: '#4a3f33',
    fontSize: '0.875rem', // 14px
    lineHeight: 1.5,
  },
  button: {
    fontWeight: 600,
    textTransform: 'none' as const,
  },
  caption: {
    fontFamily: FONT_MONO,
    fontSize: '0.75rem', // 12px
    lineHeight: 1.5,
  },
  fontFamily: FONT_BODY,
  h1: {
    '@media (min-width:1024px)': {
      fontSize: '3rem', // 48px
    },
    '@media (min-width:576px)': {
      fontSize: '2.5rem', // 40px
    },
    fontFamily: FONT_DISPLAY,
    fontSize: '2rem', // 32px
    fontWeight: 700,
    letterSpacing: '-0.03em',
    lineHeight: 1.1,
  },
  h2: {
    '@media (min-width:1024px)': {
      fontSize: '2.25rem', // 36px
    },
    '@media (min-width:576px)': {
      fontSize: '2rem', // 32px
    },
    fontFamily: FONT_DISPLAY,
    fontSize: '1.625rem', // 26px
    fontWeight: 700,
    letterSpacing: '-0.02em',
    lineHeight: 1.15,
  },
  h3: {
    '@media (min-width:1024px)': {
      fontSize: '1.75rem', // 28px
    },
    '@media (min-width:576px)': {
      fontSize: '1.5rem', // 24px
    },
    fontFamily: FONT_DISPLAY,
    fontSize: '1.375rem', // 22px
    fontWeight: 700,
    letterSpacing: '-0.02em',
    lineHeight: 1.2,
  },
  h4: {
    '@media (min-width:1024px)': {
      fontSize: '1.5rem', // 24px
    },
    '@media (min-width:576px)': {
      fontSize: '1.375rem', // 22px
    },
    fontFamily: FONT_DISPLAY,
    fontSize: '1.25rem', // 20px
    fontWeight: 700,
    letterSpacing: '-0.02em',
    lineHeight: 1.25,
  },
  h5: {
    '@media (min-width:576px)': {
      fontSize: '1.25rem', // 20px
    },
    fontFamily: FONT_DISPLAY,
    fontSize: '1.125rem', // 18px
    fontWeight: 700,
    letterSpacing: '-0.01em',
    lineHeight: 1.25,
  },
  h6: {
    fontFamily: FONT_BODY,
    fontSize: '1rem', // 16px
    fontWeight: 600,
    lineHeight: 1.25,
  },
};

const themeOptions: ThemeOptions = {
  breakpoints,
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 12px rgba(42, 32, 24, 0.06)',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        contained: {
          '&:hover': {
            boxShadow: '0 4px 12px rgba(42, 32, 24, 0.12)',
          },
          boxShadow: 'none',
        },
        root: {
          borderRadius: 8,
          padding: '8px 16px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: '1px solid #d8cfc0',
          borderRadius: 12,
          boxShadow: '0 2px 12px rgba(42, 32, 24, 0.06)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 100,
          fontFamily: FONT_MONO,
          fontSize: '0.75rem',
        },
      },
    },
    MuiDataGrid: {
      defaultProps: {
        // Material UI v7 shim — see gridBaseAutocomplete.tsx. Theme `slots` are
        // merged per-slot, so grids passing their own `slots` keep this one.
        slots: {
          baseAutocomplete: GridBaseAutocomplete,
        },
      },
      styleOverrides: {
        cell: {
          alignContent: 'center',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        rounded: {
          borderRadius: 12,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
  },
  palette: {
    background: {
      default: colors.cream,
      paper: colors.white,
    },
    divider: colors.tan,
    error: {
      main: colors.red,
    },
    grey: {
      100: colors.warm,
      200: colors.tan,
      300: colors.lightMid,
      400: colors.mid,
      50: colors.sand,
      500: colors.mid,
      600: colors.greyDarker,
      700: colors.greyDarker,
      800: colors.ink,
      900: colors.ink,
    },
    info: {
      main: colors.lightMid,
    },
    primary: {
      // Tints of the terracotta brand colour. MUI only derives light/dark/
      // contrastText, so the numeric steps the UI already asks for
      // (`primary.50` chip washes, `primary.200` soft borders) have to be
      // spelled out — without them those styles resolve to nothing.
      200: colors.cornFlowerBlue,
      50: '#f7ebe7',
      contrastText: colors.white,
      dark: colors.darkerBlue,
      light: colors.cornFlowerBlue,
      main: colors.brandPrimary,
    },
    secondary: {
      contrastText: colors.white,
      main: colors.deepPurple,
    },
    success: {
      dark: colors.green,
      light: '#e8f0eb',
      main: colors.successGreen,
    },
    text: {
      disabled: colors.mid,
      primary: colors.ink,
      secondary: colors.greyDarker,
    },
    warning: {
      // Soft wash of the warning yellow, used as a hover background.
      50: '#fff7e4',
      main: colors.yellow,
    },
  },
  shadows: [
    'none',
    '0 2px 8px rgba(42, 32, 24, 0.04)',
    '0 2px 8px rgba(42, 32, 24, 0.06)',
    '0 4px 12px rgba(42, 32, 24, 0.06)',
    '0 4px 12px rgba(42, 32, 24, 0.08)',
    '0 6px 16px rgba(42, 32, 24, 0.08)',
    '0 6px 16px rgba(42, 32, 24, 0.1)',
    '0 8px 24px rgba(42, 32, 24, 0.1)',
    '0 8px 24px rgba(42, 32, 24, 0.1)',
    '0 8px 32px rgba(42, 32, 24, 0.12)',
    '0 8px 32px rgba(42, 32, 24, 0.12)',
    '0 8px 48px rgba(42, 32, 24, 0.12)',
    '0 8px 48px rgba(42, 32, 24, 0.12)',
    '0 8px 48px rgba(42, 32, 24, 0.14)',
    '0 8px 48px rgba(42, 32, 24, 0.14)',
    '0 8px 48px rgba(42, 32, 24, 0.16)',
    '0 8px 48px rgba(42, 32, 24, 0.16)',
    '0 8px 48px rgba(42, 32, 24, 0.18)',
    '0 8px 48px rgba(42, 32, 24, 0.18)',
    '0 8px 48px rgba(42, 32, 24, 0.2)',
    '0 8px 48px rgba(42, 32, 24, 0.2)',
    '0 8px 48px rgba(42, 32, 24, 0.22)',
    '0 8px 48px rgba(42, 32, 24, 0.22)',
    '0 8px 48px rgba(42, 32, 24, 0.24)',
    '0 8px 48px rgba(42, 32, 24, 0.24)',
  ],
  shape: {
    borderRadius: 8,
  },
  spacing,
  typography,
};

// Create and export the theme
export const theme = createTheme(themeOptions);

// Export colors for use in components that still need direct access
export { colors };

// Type augmentation for custom theme properties
declare module '@mui/material/styles' {
  interface Theme {
    customColors: typeof colors;
  }
  interface ThemeOptions {
    customColors?: typeof colors;
  }
}

// Add custom colors to theme
export const themeWithCustomColors = createTheme({
  ...themeOptions,
  customColors: colors,
});

export default themeWithCustomColors;
